import {usePromotion} from '@/context/Promotion/PromotionContext'
import {selectCountryCode} from '@/store/slices/countryCodeSlice'
import {CurrencyType} from '@/types/promotion'
import {COUNTRY_CODES} from '@/utils/constants/countryConstants'
import {ANY_BUY_OPTION, ANY_ID} from '@/utils/constants/promotionConstants'
import {useMemo, useState} from 'react'
import {useSelector} from 'react-redux'
import {useRewardValidations} from './Rewards/RewardValidations'

const defaultCondition = {
  type: 'BUY',
  items: [],
}

const rowLabels = ['Buy', 'and buy']

const withoutAny = options => options?.filter(item => item.id !== ANY_ID)

// Turns on/off one reward per buy condition across every layer, since the toggle is a promotion-wide setting
const toggleIndividualRewards = (promotion, checked) => ({
  ...promotion,
  promotionConditions: promotion.promotionConditions.map(condition => {
    const conditions = condition.conditionGroup?.conditions ?? []
    const [firstReward] = condition.rewards ?? []
    // Individual rewards share the first reward's type, so new ones are seeded with it
    const seedReward = firstReward?.type ? {type: firstReward.type, discountType: firstReward.discountType} : {}

    return {
      ...condition,
      conditionGroup: {
        ...condition.conditionGroup,
        conditions: conditions.map((cond, idx) => (idx === 0 ? {...cond, hasIndividualRewards: checked} : cond)),
      },
      rewards: checked
        ? conditions.map((_, idx) => condition.rewards?.[idx] ?? (idx === 0 ? {} : seedReward))
        : (condition.rewards ?? []).slice(0, 1),
    }
  }),
})

export const useBuyMultipleProductsCondition = ({promotionCondition, selectedPromotionCondition}) => {
  const {updatePromotion, promotionConfig, isLoadingConfig} = usePromotion()
  const {hasBuyQuantityLayerError} = useRewardValidations(promotionCondition)
  const countryCode = useSelector(selectCountryCode)
  const [modalProps, setModalProps] = useState({show: false, type: '', index: 0})

  const isAmountTab = selectedPromotionCondition === 'EA'
  const currencyCode = CurrencyType[countryCode ?? 'MX'] ?? '$'
  // Individual rewards are currently only available for Turkey
  const isIndividualRewardsFeatureEnabled = countryCode === COUNTRY_CODES.TR

  const buyQtyOptions = useMemo(
    () => promotionConfig?.buyQty?.map(value => ({displayText: value, id: Number(value)})),
    [promotionConfig?.buyQty]
  )
  const buyTypeOptions = promotionConfig?.conditions?.itemTypeOptions ?? []

  const [rawCond1, rawCond2] = promotionCondition?.conditionGroup?.conditions || []
  const conditions = [rawCond1 ?? defaultCondition, rawCond2 ?? defaultCondition]
  const hasIndividualRewards = isIndividualRewardsFeatureEnabled && Boolean(conditions[0].hasIndividualRewards)

  // Everything a single "buy <quantity> <products>" row needs to render
  const rows = conditions.map((cond, condIndex) => {
    const buyCount = isAmountTab ? cond.amount : cond.quantity
    const items = cond.items ?? []
    const productsValue = items.map(item => ({id: item.id, displayText: item.name}))
    const quantityValue = buyQtyOptions?.filter(item => item.id === buyCount) ?? []

    return {
      condIndex,
      label: rowLabels[condIndex],
      condition: cond,
      buyCount,
      productsValue,
      quantityValue,
      selectedValuesText: items.map(item => item.name).join(', '),
      // ANY can't be picked for both the quantity and the products of the same condition
      qtyOptions: productsValue.some(value => value.id === ANY_ID) ? withoutAny(buyQtyOptions) : buyQtyOptions,
      typeOptions: quantityValue.some(value => value.id === ANY_ID) ? withoutAny(buyTypeOptions) : buyTypeOptions,
      isError: hasBuyQuantityLayerError(condIndex),
    }
  })

  const updateConditionByIndex = (update, conditionIndex = 0) => {
    updatePromotion(prev => ({
      ...prev,
      promotionConditions: prev.promotionConditions.map(condition => {
        if (condition.sequence !== promotionCondition.sequence) return condition

        const currentConditions = condition.conditionGroup?.conditions ?? []
        const nextConditions = [...currentConditions]
        while (nextConditions.length <= conditionIndex) {
          nextConditions.push(defaultCondition)
        }

        return {
          ...condition,
          conditionGroup: {
            ...condition.conditionGroup,
            conditions: nextConditions.map((cond, idx) => (idx === conditionIndex ? {...cond, ...update} : cond)),
          },
        }
      }),
    }))
  }

  const handleBuyCount = (values, condIndex) => {
    const value = values?.[0]?.id
    if (!value) return

    // If ANY is selected in the quantity dropdown, set quantity to 1
    const nextValue = value === ANY_ID ? 1 : Number(value)
    updateConditionByIndex(isAmountTab ? {amount: nextValue} : {quantity: nextValue}, condIndex)
  }

  const handleBuyAmount = (value, condIndex) => {
    updateConditionByIndex({amount: value ? Number(value) : null}, condIndex)
  }

  const clearValues = condIndex => {
    updateConditionByIndex({items: [], itemType: null}, condIndex)
  }

  const handleValues = (selectedItemsAndType, productCombination, condIndex) => {
    const {values, itemType} = selectedItemsAndType || {}
    if (!values || !Array.isArray(values)) {
      clearValues(condIndex)
      return
    }
    const items = values.map(item => ({id: item?.id, name: item?.displayText, uom: selectedPromotionCondition}))

    updateConditionByIndex({items, productCombination, itemType}, condIndex)
  }

  // Selecting a product either applies it directly or opens the selection modal for modal-backed types
  const handleSelectedBuyItem = (values, condIndex) => {
    if (!values || !values.length) {
      clearValues(condIndex)
      return
    }

    const isModalValue = values.find(item => buyTypeOptions.find(({id}) => id === item.id))

    if (!isModalValue) {
      handleValues({values, itemType: conditions[condIndex].itemType}, null, condIndex)
    } else if (isModalValue.id === ANY_ID) {
      handleValues({values: [ANY_BUY_OPTION], itemType: ANY_ID}, null, condIndex)
    } else {
      setModalProps({show: true, type: isModalValue.id, index: condIndex})
    }
  }

  const handleCloseModal = () => {
    setModalProps(prev => ({...prev, show: false}))
  }

  const handleSubmitModal = (selectedItems, combination) => {
    handleValues(selectedItems, combination, modalProps.index)
    handleCloseModal()
  }

  const handleToggleIndividualRewards = checked => {
    updatePromotion(prev => toggleIndividualRewards(prev, checked))
  }

  const modalRow = rows[modalProps.index]

  return {
    isLoadingConfig,
    isAmountTab,
    currencyCode,
    isIndividualRewardsFeatureEnabled,
    hasIndividualRewards,
    rows,
    modalProps,
    modalConditionValues: {
      values: modalRow.productsValue,
      itemType: modalRow.condition.itemType,
      productCombination: modalRow.condition.productCombination,
    },
    handleBuyCount,
    handleBuyAmount,
    handleSelectedBuyItem,
    handleToggleIndividualRewards,
    handleSubmitModal,
    handleCloseModal,
  }
}
